import { QueryClient } from '@tanstack/react-query';

/** Exported so tests can build a client from the production defaults instead of copying them by hand. */
export function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Data is fresh for 5 minutes - prevents refetches when navigating back
        staleTime: 5 * 60 * 1000,
        // Keep unused data in cache for 30 minutes
        gcTime: 30 * 60 * 1000,
        // Don't refetch on window focus (mobile-friendly)
        refetchOnWindowFocus: false,
        // Retry failed requests twice
        retry: 2,
      },
      mutations: {
        retry: 0,
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined = undefined;

export function getQueryClient() {
  if (typeof window === 'undefined') {
    // Server-side: always create a new QueryClient
    return makeQueryClient();
  }
  // Browser-side: reuse the same QueryClient
  if (!browserQueryClient) {
    browserQueryClient = makeQueryClient();
  }
  return browserQueryClient;
}

export const queryClient = getQueryClient();

// Exposes the client for E2E tests (see tests/helpers/gate.ts's
// completeTourViaAPI) to force deterministic cache invalidation — e.g.
// simulating a background refetch — without waiting out staleTime. DEV-only:
// `vite --mode test` (playwright.config.ts's webServer) runs the dev server,
// so this is present for E2E but stripped from production builds.
if (import.meta.env.DEV) {
  (window as unknown as { __queryClient: typeof queryClient }).__queryClient = queryClient;
}
